/* eslint-disable css-modules/no-undef-class */
import { Feature } from 'geojson';
import dynamic from 'next/dynamic';
import React, { useMemo, useRef, useState } from 'react';
import Image from '@/components/atoms/image/image';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import Row from '@/components/utilities/row/row';
import useQueryCountriesMap from '@/integrations/offers/countries/map/map.hooks';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import CountryMapDetails from './country-map-details/country-map-details';
import {
  countryMapFeatureAdapter,
  countryMapFeatureCollectionAdapter,
} from './country-map.adapters';
import styles from './country-map.module.scss';

const Map = dynamic(() => import('@/components/molecules/map/map'), {
  loading: () => <SkeletonLoader height="100%" width="100%" />,
  ssr: false,
});

export interface CountryMapProps extends ModuleWrapperProps {
  /** The country id */
  countryId: number;
  /** The text to show as a headline */
  headline: string | null;
  /** The image to show as a placeholder */
  placeholderImage: Content.Properties.Image | null;
  /** The text to show as a pre headline */
  preHeadline: string | null;
}

const CountryMap = ({
  preHeadline,
  countryId,
  headline,
  placeholderImage,
  ...rest
}: CountryMapProps) => {
  const { t } = useI18n();
  const isTablet = useWindowSize('tablet');
  const [activeItem, setActiveItem] = useState<number>();
  const [showMap, setShowMap] = useState(false);
  const dataContainerRef = useRef<HTMLDivElement>(null);
  const mapWrapperRef = useRef<HTMLDivElement>(null);

  const { data: dataCountriesMap } = useQueryCountriesMap({
    countryId,
  });

  const handleClickMap = (feature: Feature | any) => {
    if (!dataCountriesMap) return;

    const item = dataCountriesMap.find(
      map => String(map.destinationId) === String(feature.properties.id),
    );

    if (!item) return;

    const indexOf = dataCountriesMap.indexOf(item);

    if (typeof indexOf !== 'number') return;

    setActiveItem(indexOf);
  };

  const formattedMapData = useMemo(() => {
    return countryMapFeatureCollectionAdapter(dataCountriesMap);
  }, [dataCountriesMap]);

  return (
    <ModuleWrapper {...rest} className={styles.component}>
      {(preHeadline || headline) && (
        <Container>
          <Row>
            <Column className={styles.textColumn} width={{ laptop: 8 }}>
              {preHeadline && (
                <Typography
                  className={styles.preHeadline}
                  tagStyle="subHeading"
                >
                  {preHeadline}
                </Typography>
              )}

              {headline && (
                <Typography
                  className={styles.headline}
                  tagStyle="displayMedium"
                >
                  {headline}
                </Typography>
              )}
            </Column>
          </Row>
        </Container>
      )}

      <div ref={dataContainerRef}>
        <Container>
          <Row>
            <Column className={styles.mapColumn}>
              <div ref={mapWrapperRef} className={styles.mapWrapper}>
                {dataCountriesMap &&
                  (!placeholderImage || showMap ? (
                    <React.Fragment>
                      <Map
                        activeItem={
                          typeof activeItem === 'number' &&
                          dataCountriesMap[activeItem]
                            ? countryMapFeatureAdapter(
                                dataCountriesMap[activeItem],
                              )
                            : undefined
                        }
                        controlPosition={isTablet ? 'bottom-right' : undefined}
                        items={{ markers: formattedMapData }}
                        onClickMarker={handleClickMap}
                      />
                    </React.Fragment>
                  ) : (
                    <div className={styles.placeholder}>
                      <Image
                        alt={placeholderImage.altText}
                        className={styles.placeholderImage}
                        fill
                        height={459}
                        src={placeholderImage.url}
                        srcSet={{
                          desktop: {
                            height: 756,
                            width: 1344,
                          },
                          laptop: {
                            height: 532,
                            width: 945,
                          },
                          tablet: {
                            height: 517,
                            width: 720,
                          },
                        }}
                        unoptimized
                        width={328}
                      />

                      <Button
                        className={styles.placeholderButton}
                        onClick={() => setShowMap(true)}
                        text={t('items.common.words.showMap')}
                      />
                    </div>
                  ))}
              </div>
            </Column>
          </Row>
        </Container>
      </div>

      <CountryMapDetails
        data={
          typeof activeItem === 'number' && dataCountriesMap
            ? dataCountriesMap[activeItem].destinationCard
            : undefined
        }
        isOpen={typeof activeItem === 'number'}
        onClose={() => setActiveItem(undefined)}
      />
    </ModuleWrapper>
  );
};

CountryMap.displayName = 'CountryMap';

export default CountryMap;
